<script lang="ts">
    import { _ } from "svelte-i18n";
    import { createEventDispatcher } from "svelte";
    import Dialog, { Title, Content, Actions } from "@smui/dialog";
    import DataTable, { Head, Body, Row, Cell, Label, Pagination } from "@smui/data-table";
    import Select, { Option } from "@smui/select";
    import IconButton, { Icon } from "@smui/icon-button";
    import CircularProgress from "@smui/circular-progress";
    import Button from "@smui/button";
    import { format } from "date-fns";

    export let open = false;
    export let weekHistoryData = [];
    export let weekData = {};
    export let loadingHistoryData = false;

    const dispatch = createEventDispatcher();

    // Paging
    let rowsPerPage = 10;
    let currentPage = 0;

    // Define table head
    let tableHead = [
        { columnId: "actionTime", label: "action_time", numeric: false, extraClasses: "right-splitter text-center" },
        { columnId: "user", label: "user", numeric: false, extraClasses: "right-splitter text-center" },
        { columnId: "qsDeadlineDate", label: "qs_deadline", numeric: false, extraClasses: "wrap-spaces right-splitter text-center" },
        { columnId: "nQsDeadlineNotifDate", label: "qs_deadline_notification", numeric: true, extraClasses: "wrap-spaces right-splitter text-center" },
        { columnId: "buyingDeadlineDate", label: "buying_deadline", numeric: true, extraClasses: "wrap-spaces right-splitter text-center" },
        { columnId: "amendmentNotifTime", label: "amendment_notif_time", numeric: true, extraClasses: "wrap-spaces right-splitter text-center"},
        { columnId: "status", label: "status", numeric: false, extraClasses: "right-splitter text-center" },
    ];

    $: start = currentPage * rowsPerPage;
    $: end = Math.min(start + rowsPerPage, weekHistoryData.length);
    $: slice = weekHistoryData.slice(start, end);
    $: lastPage = Math.max(Math.ceil(weekHistoryData.length / rowsPerPage) - 1, 0);

    $: if (currentPage > lastPage) {
        currentPage = lastPage;
    }
</script>

<Dialog
    bind:open
    scrimClickAction=""
    escapeKeyAction=""
    aria-labelledby="sa-history-title"
    aria-describedby="sa-history-content"
    class="pa-2"
    surface$style="width: 1900px; max-width: calc(100vw - 32px)"
>
    <Title id="sa-history-title" class="tescoblue white-text">
        {$_("week")}: {weekHistoryData[0]?.description} | {(new Date(weekData.weekStart)).toAppDate()} - {(new Date(weekData.weekEnd)).toAppDate()}
    </Title>
    <Content id="sa-history-content">
        <DataTable table$aria-label="Audit Data" class="report" style="width: 100%; max-height: 550px;">
            <Head>
                <Row>
                    {#each tableHead as head}
                        <Cell class="datatable-header {head.extraClasses}"><Label>{$_(head.label)}</Label></Cell>
                    {/each}
                </Row>
            </Head>
            <Body>
                {#if loadingHistoryData}
                    <Row>
                        <Cell colspan={tableHead.length} style="text-align: center;">
                            <CircularProgress indeterminate style="height: 32px; width: 32px; margin: auto" aria-label="Data is being loaded..." />
                        </Cell>
                    </Row>
                {:else}
                    {#each slice as item}
                        <Row>
                            <Cell class="text-center right-splitter-sub">{(new Date(item.operationDateTime)).toAppDateTimeSec() ?? ""}</Cell>
                            <Cell class="text-left right-splitter-sub">{item.operationUser ?? ""}</Cell>
                            <Cell class="text-center right-splitter-sub">
                                {#if item.newQsDeadlineDate && item.newQsDeadlineDate != item.qsDeadlineDate}
                                    <Button class="custom-button" disabled>
                                        <Label class="font-weight-regular">{@html (new Date(item.qsDeadlineDate)).toAppDateTimeBr() ?? ""}</Label>
                                        &nbsp;&nbsp;<Icon class="material-icons tescoblue-text" style="margin: 0px">arrow_forward</Icon>&nbsp;&nbsp;
                                        <Label class="font-weight-regular">{@html (new Date(item.newQsDeadlineDate)).toAppDateTimeBr()}</Label>
                                    </Button>
                                {/if}
                            </Cell>

                            <Cell class="text-center right-splitter-sub">
                                {#if item.newQsDeadlineNotifDate && item.qsDeadlineNotifDate != item.newQsDeadlineNotifDate}
                                    <Button class="custom-button" disabled>
                                        <Label class="font-weight-regular">{@html (new Date(item.qsDeadlineNotifDate)).toAppDateTimeBr() ?? ""}</Label>
                                        &nbsp;&nbsp;<Icon class="material-icons tescoblue-text" style="margin: 0px">arrow_forward</Icon>&nbsp;&nbsp;
                                        <Label class="font-weight-regular">{@html (new Date(item.newQsDeadlineNotifDate)).toAppDateTimeBr()}</Label>
                                    </Button>
                                {/if}
                            </Cell>

                            <Cell class="text-center right-splitter-sub">
                                {#if item.newBuyingDeadlineDate && item.buyingDeadlineDate != item.newBuyingDeadlineDate}
                                    <Button class="custom-button" disabled>
                                        <Label class="font-weight-regular">{@html (new Date(item.buyingDeadlineDate)).toAppDateTimeBr() ?? ""}</Label>
                                        &nbsp;&nbsp;<Icon class="material-icons tescoblue-text" style="margin: 0px">arrow_forward</Icon>&nbsp;&nbsp;
                                        <Label class="font-weight-regular">{@html (new Date(item.newBuyingDeadlineDate)).toAppDateTimeBr()}</Label>
                                    </Button>
                                {/if}
                            </Cell>

                            <Cell class="text-center right-splitter-sub">
                                {#if item.newAmendmentNotifTime && item.amendmentNotifTime != item.newAmendmentNotifTime}
                                    <Button class="custom-button" disabled>
                                        <Label class="font-weight-regular">{item.amendmentNotifTime ?? ""}</Label>
                                        &nbsp;&nbsp;<Icon class="material-icons tescoblue-text" style="margin: 0px">arrow_forward</Icon>&nbsp;&nbsp;
                                        <Label class="font-weight-regular">{item.newAmendmentNotifTime}</Label>
                                    </Button>
                                {/if}
                            </Cell>

                            <Cell class="text-center right-splitter-sub">
                                {#if item.newStatus && item.status != item.newStatus}
                                    <Button class="custom-button" disabled>
                                        <Label class="font-weight-regular">{$_(item.status) ?? ""}</Label>
                                        &nbsp;&nbsp;<Icon class="material-icons tescoblue-text" style="margin: 0px">arrow_forward</Icon>&nbsp;&nbsp;
                                        <Label class="font-weight-regular">{$_(item.newStatus)}</Label>
                                    </Button>
                                {/if}
                            </Cell>



                        </Row>
                    {/each}
                {/if}
            </Body>
            <Pagination slot="paginate">
                <svelte:fragment slot="rowsPerPage">
                    <Label>{$_("rows_per_page")}</Label>
                    <Select variant="outlined" bind:value={rowsPerPage} noLabel>
                        <Option value={10}>10</Option>
                        <Option value={25}>25</Option>
                        <Option value={100}>100</Option>
                    </Select>
                </svelte:fragment>
                <svelte:fragment slot="total">
                    {start + 1}-{end} of {weekHistoryData.length}
                </svelte:fragment>

                <IconButton class="material-icons" action="first-page" title="First page" on:click={() => (currentPage = 0)} disabled={currentPage === 0}>first_page</IconButton>
                <IconButton class="material-icons" action="prev-page" title="Prev page" on:click={() => currentPage--} disabled={currentPage === 0}>chevron_left</IconButton>
                <IconButton class="material-icons" action="next-page" title="Next page" on:click={() => currentPage++} disabled={currentPage === lastPage}>chevron_right</IconButton
                >
                <IconButton class="material-icons" action="last-page" title="Last page" on:click={() => (currentPage = lastPage)} disabled={currentPage === lastPage}
                    >last_page</IconButton
                >
            </Pagination>
        </DataTable>
    </Content>
    <Actions class="pr-4 gap-1 space-around">
        <Button action="close" class="tescoblue white-text" on:click={() => dispatch("closeHistoryDialog")}>
            <Label>{$_("close")}</Label>
        </Button>
    </Actions>
</Dialog>
